import { logger } from "@platform/browser-logger";
import { AxiosInstance } from "axios";

import {
  CreateFieldCommentRequest,
  FieldCommentResponse,
  UpdateFieldCommentRequest,
  UpsertFieldCommentsRequest,
  UpsertFieldCommentsResponse,
} from "./fieldCommentTypes";

const basePath = (benefitPlanId: string): string =>
  `btx/v1/benefit-plans/${benefitPlanId}/field-comments`;

export const listFieldComments = async (
  benefitPlanId: string,
  axiosInstance: AxiosInstance,
): Promise<FieldCommentResponse[]> => {
  logger.debug("[listFieldComments] Loading field comments", { benefitPlanId });
  const response = await axiosInstance.get<FieldCommentResponse[]>(
    basePath(benefitPlanId),
  );
  return response.data;
};

export const createFieldComment = async (
  benefitPlanId: string,
  data: CreateFieldCommentRequest,
  axiosInstance: AxiosInstance,
): Promise<FieldCommentResponse> => {
  logger.debug("[createFieldComment] Creating field comment", {
    benefitPlanId,
    fieldPath: data.fieldPath,
  });
  const response = await axiosInstance.post<FieldCommentResponse>(
    basePath(benefitPlanId),
    data,
  );
  return response.data;
};

export const updateFieldComment = async (
  benefitPlanId: string,
  fieldCommentId: string,
  data: UpdateFieldCommentRequest,
  axiosInstance: AxiosInstance,
): Promise<FieldCommentResponse> => {
  logger.debug("[updateFieldComment] Updating field comment", {
    benefitPlanId,
    fieldCommentId,
  });
  const response = await axiosInstance.patch<FieldCommentResponse>(
    `${basePath(benefitPlanId)}/${fieldCommentId}`,
    data,
  );
  return response.data;
};

export const deleteFieldComment = async (
  benefitPlanId: string,
  fieldCommentId: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  logger.debug("[deleteFieldComment] Deleting field comment", {
    benefitPlanId,
    fieldCommentId,
  });
  await axiosInstance.delete(`${basePath(benefitPlanId)}/${fieldCommentId}`);
};

/**
 * Writes field comments across several benefit plans of one RFP in a single
 * transaction, creating the comment on any field that has none yet.
 */
export const upsertFieldComments = async (
  rfpId: string,
  data: UpsertFieldCommentsRequest,
  axiosInstance: AxiosInstance,
): Promise<UpsertFieldCommentsResponse> => {
  const response = await axiosInstance.patch<UpsertFieldCommentsResponse>(
    `btx/v1/rfps/${rfpId}/field-comments`,
    data,
  );
  return response.data;
};
